import React, { useState } from 'react'
import { useForm, FieldValues } from 'react-hook-form'
import { Link, useNavigate, useLocation } from 'react-router'
import { Alert, Button, Input, Logo, useUserService } from '@climate/components'
import type { LoginResponse, LoginErrorBody, MfaChallengeResponse } from '../types'
import { landAfterSignIn } from '../signIn'
import LogoImage from '../../assets/images/logo.png'

export default function Login() {
  const navigate = useNavigate()
  const location = useLocation()
  const { register, handleSubmit, formState: { errors } } = useForm()
  const { login } = useUserService(window.config.iam_service_url, window.config.session_cookie, 'iam')
  const [remoteErrors, setRemoteErrors] = useState<string | undefined>()

  const loginAction = async (data: FieldValues) => {
    setRemoteErrors(undefined)

    try {
      let validationToken: string | null = null
      if (window.config.google_recaptcha_site_key) {
        validationToken = await grecaptcha.execute(window.config.google_recaptcha_site_key, {action: 'login'})
      }

      const result = await login({...data, validationToken} as { email: string; password: string; validationToken: string }) as LoginResponse | MfaChallengeResponse

      // The password was right but sign-in is not finished. Nothing is stored
      // and no cookie has been set yet.
      //
      // The handover travels in router state, which means history.state, which
      // browsers do persist across a reload and into session restore. So it is
      // not as ephemeral as it looks: what keeps it safe is the ten-minute
      // expiry and the fact that it is useless without a code, not the
      // transport. It is still preferable to localStorage, which would outlive
      // the tab and be readable by any script on the page.
      if ('mfa_required' in result) {
        navigate(result.mfa_enrolled ? '/app/mfa' : '/app/mfa/setup', {
          replace: true,
          state: { mfaToken: result.mfa_token, search: location.search },
        })
        return
      }

      landAfterSignIn(result, location.search, navigate)
    } catch (e: unknown) {
      const err = e as { data: { text: () => Promise<string> } }
      const { message, error }: LoginErrorBody = JSON.parse(await err.data.text())
      setRemoteErrors(message?.[0] || error)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-sm">
        <Logo LogoImage={LogoImage} alt="Tellus Iam logo"/>

        {location.search.includes('confirmed') && (
          <div className="mt-6">
            <Alert type="success" message="Email verified"/>
          </div>
        )}

        <h2 className="mt-6 text-center text-2xl font-bold text-gray-900">Sign in to your account</h2>

        <div className="mt-8">
          <div className="bg-white py-8 px-4 shadow-sm sm:rounded-lg sm:px-10">
            <form id="sign_in_form" className="space-y-6" onSubmit={handleSubmit(loginAction)}>
              <div>
                <label htmlFor="email" className="pb-2 block text-sm text-gray-700">
                  Email address
                </label>
                <Input
                  id="email"
                  name="email"
                  register={register('email', { required: 'Required', })}
                  errors={errors}
                  autoComplete="email"
                />
              </div>

              <div>
                <label htmlFor="password" className="pb-2 block text-sm text-gray-700">
                  Password
                </label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  register={register('password', { required: 'Required', })}
                  errors={errors}
                  autoComplete="password"
                />
              </div>

              {remoteErrors &&
                <Alert type="danger" message={remoteErrors}/>
              }

              <div className="py-2">
                <Button
                  id="sign_in"
                  className="w-full justify-center"
                  type="submit"
                  size={3}
                >
                  Sign in
                </Button>
              </div>
            </form>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"/>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">
                    {'Don\'t have an account?'}&nbsp;
                    <Link to="/app/register"
                      className="font-medium text-perfect-shine hover:text-perfect-shine-light">Sign up</Link>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
